import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { animeNews } from "@/mocks/news";
import Tag from "@/components/base/Tag";
import Breadcrumb from "@/components/base/Breadcrumb";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareModal from "@/components/base/ShareModal";
import NewsCard from "@/components/base/NewsCard";
import CommentSection from "@/components/feature/CommentSection";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const news = animeNews.find((n) => n.id === id);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: news ? `${news.title} - 萝莉动漫主题站` : "文章不存在 - 萝莉动漫主题站",
    description: news ? news.summary : "该文章不存在或已被移除。",
    keywords: news ? news.tags.join(",") : "萝莉资讯"
  });

  const related = useMemo(() => {
    if (!news) return [];
    return animeNews
      .filter((n) => n.id !== news.id)
      .sort((a, b) => {
        const sa = (a.category === news.category ? 2 : 0) + b.popularity / 100;
        const sb = (b.category === news.category ? 2 : 0) + a.popularity / 100;
        return sb - sa;
      })
      .slice(0, 4);
  }, [news]);

  if (!news) {
    return (
      <div className="mx-auto flex max-w-[900px] flex-col items-center justify-center px-6 py-24 text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 text-primary-500">
          <i className="ri-article-line text-4xl"></i>
        </div>
        <h1 className="text-2xl text-foreground-950">文章不存在</h1>
        <p className="mt-3 text-sm text-foreground-600">
          这篇资讯可能已经被移除或链接有误，去看看其他最新的动漫资讯吧。
        </p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "动漫资讯", to: "/news" },
          { label: news.title }
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <article className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <Tag tone="primary">{news.category}</Tag>
            <span className="text-sm text-foreground-500">{news.date}</span>
            <span className="text-sm text-foreground-500">·</span>
            <span className="text-sm text-foreground-500">{news.author}</span>
          </div>

          <h1 className="mt-3 text-2xl leading-snug text-foreground-950 md:text-3xl">
            {news.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {news.views.toLocaleString()} 次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line"></i>
              热度 {news.popularity}
            </span>
          </div>

          <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <div className="aspect-[16/9] w-full">
              <img
                src={news.thumbnail}
                alt={`${news.title} 资讯配图`}
                title={`${news.title} - 萝莉动漫资讯`}
                className="h-full w-full object-top"
              />
            </div>
          </div>

          <div className="mt-6 space-y-5">
            {news.content.map((para, i) => (
              <div key={`para-${i}`}>
                <p className="text-base leading-loose text-foreground-700">{para}</p>
                {i === 1 && (
                  <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-100">
                    <div className="aspect-[16/9] w-full">
                      <img
                        src="https://readdy.ai/api/search-image?query=anime%20illustration%20of%20cozy%20pastel%20scene%20with%20cute%20anime%20girl%20reading%20book%2C%20soft%20pink%20and%20lavender%20palette%2C%20warm%20ambient%20lighting%2C%20high%20detail&width=1000&height=560&seq=news-inline-01&orientation=landscape"
                        alt={`${news.title} 正文插画`}
                        title="萝莉动漫资讯正文插画"
                        className="h-full w-full object-top"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {news.tags.map((t) => (
              <Link
                key={t}
                to={`/news?q=${encodeURIComponent(t)}`}
                className="rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                # {t}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 border-y border-background-200 py-5">
            <FavoriteButton type="news" id={news.id} withLabel />
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>

          <CommentSection articleId={news.id} />

          <div className="mt-10">
            <h2 className="mb-4 text-xl text-foreground-950">相关文章推荐</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {related.map((n) => (
                <NewsCard key={n.id} news={n} />
              ))}
            </div>
          </div>
        </article>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base text-foreground-950">
              <i className="ri-fire-line text-accent-500"></i>
              热门阅读
            </h3>
            <div className="space-y-1">
              {[...animeNews]
                .sort((a, b) => b.views - a.views)
                .slice(0, 5)
                .map((n) => (
                  <NewsCard key={n.id} news={n} variant="compact" />
                ))}
            </div>
          </div>

          <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
            <h3 className="mb-2 flex items-center gap-2 font-heading text-base text-foreground-950">
              <i className="ri-heart-3-line text-primary-500"></i>
              喜欢这篇内容？
            </h3>
            <p className="text-sm leading-relaxed text-foreground-700">
              收藏本站，第一时间获取最新的萝莉动漫资讯与角色资料。
            </p>
            <Link
              to="/news"
              className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              查看更多资讯
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`${news.title} - 萝莉动漫主题站`}
        path={`/news/${news.id}`}
      />
    </div>
  );
}